import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
} from '@chakra-ui/react';
import Breadcrumbs from '../shared/Breadcrumbs';
import CrosstabBuilder from './CrosstabBuilder';
import WeightingPanel from './WeightingPanel';
import QualityPanel from './QualityPanel';
import ExportBuilder from './ExportBuilder';
import OverviewPanel from './OverviewPanel';
import fetchWithAuth from '../../utils/fetchWithAuth';

/**
 * AnalyticsDashboard - Main analytics interface for poll results
 *
 * Provides professional-grade data analysis tools:
 * - Overview: Summary statistics and visualizations
 * - Crosstabs: Demographic breakdowns with statistical tests
 * - Weighting: Demographic weighting configuration
 * - Quality: Response quality analysis
 * - Export: Multi-format data export
 */
function AnalyticsDashboard() {
  const { pollId } = useParams();
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchPollData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId]);

  const fetchPollData = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`/api/polls/${pollId}/`);

      if (!response.ok) {
        throw new Error(`Failed to fetch poll: ${response.statusText}`);
      }

      const data = await response.json();
      setPoll(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching poll:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text>Loading analytics dashboard...</Text>
        </VStack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error" variant="left-accent">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>Error Loading Poll</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Box>
        </Alert>
      </Container>
    );
  }

  if (!poll) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="warning">
          <AlertIcon />
          Poll not found
        </Alert>
      </Container>
    );
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Polls', href: '/pollsPage' },
    { label: poll.title, href: `/poll-results/${pollId}` },
    { label: 'Analytics', href: `/analytics/${pollId}` }
  ];

  return (
    <Box bg="background.shade1" minH="100vh">
      <Container maxW="container.xl" py={6}>
        <Breadcrumbs items={breadcrumbItems} />

        {/* Header */}
        <Box bg="white" p={6} borderRadius="lg" shadow="sm" mb={6}>
          <HStack justify="space-between" align="start">
            <VStack align="start" spacing={2}>
              <Heading size="lg">{poll.title}</Heading>
              <HStack spacing={3}>
                <Badge colorScheme={
                  poll.state === 'live' ? 'green' :
                  poll.state === 'closed' ? 'red' :
                  poll.state === 'staged' ? 'orange' :
                  'gray'
                }>
                  {poll.state.toUpperCase()}
                </Badge>
                <Text color="theme.mediumGray" fontSize="sm">
                  Poll ID: {poll.id}
                </Text>
              </HStack>
            </VStack>

            <Button
              colorScheme="blue"
              variant="outline"
              size="sm"
              onClick={fetchPollData}
            >
              Refresh Data
            </Button>
          </HStack>

          {poll.description && (
            <Text color="gray.600" mt={3}>
              {poll.description}
            </Text>
          )}
        </Box>

        {/* Main Tabs */}
        <Box bg="white" borderRadius="lg" shadow="sm">
          <Tabs
            index={activeTab}
            onChange={setActiveTab}
            variant="enclosed"
            colorScheme="blue"
            isLazy
          >
            <TabList borderBottomWidth="2px">
              <Tab fontWeight="medium">Overview</Tab>
              <Tab fontWeight="medium">Crosstabs</Tab>
              <Tab fontWeight="medium">Weighting</Tab>
              <Tab fontWeight="medium">Quality</Tab>
              <Tab fontWeight="medium">Export</Tab>
            </TabList>

            <TabPanels>
              {/* Overview Panel */}
              <TabPanel p={6}>
                <OverviewPanel poll={poll} pollId={pollId} />
              </TabPanel>

              {/* Crosstabs Panel */}
              <TabPanel p={6}>
                <CrosstabBuilder poll={poll} pollId={pollId} />
              </TabPanel>

              {/* Weighting Panel */}
              <TabPanel p={6}>
                <WeightingPanel poll={poll} pollId={pollId} />
              </TabPanel>

              {/* Quality Panel */}
              <TabPanel p={6}>
                <QualityPanel poll={poll} pollId={pollId} />
              </TabPanel>

              {/* Export Panel */}
              <TabPanel p={6}>
                <ExportBuilder poll={poll} pollId={pollId} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>

        {/* Help Footer */}
        <Box mt={6} p={4} bg="background.shade2" borderRadius="md" border="1px solid" borderColor="border.light">
          <Text fontSize="sm" color="theme.darkGray">
            <strong>Analytics Help:</strong> Use the tabs above to analyze your poll data.
            Generate crosstabs to see demographic breakdowns, apply weighting to adjust for
            sample bias, review response quality, and export your data in multiple formats.
          </Text>
        </Box>
      </Container>
    </Box>
  );
}

export default AnalyticsDashboard;
